import React, { useEffect, useState } from 'react';
import CustomSplitColumnCard from '@common/components/misc/CustomSplitColumnCard';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { PasswordIcon, RobotIcon, UsersIcon } from '@common/utils/icons/icons';
import SwitchInput from '../../forms/SwitchInput';
import { useAppDispatch, useAppSelector } from '../../../store';
import { selectOrganizationsData } from '../../../store/organization/organization-slice-selectors';
import { patchOrganizationById } from '../../../store/organization/organization-actions';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { resetOrganizationsRestState } from '../../../store/organization/organization-slice';
import { selectUser } from '../../../store/general/general-slice-selectors';
import OrganizationRole from '../../../models/enums/organization-roles';
import ConfirmAbortDialog from '@common/components/General/ConfirmAbortDialog';
import { removeToken } from 'services/data/axios-base';
import LoginVariant from 'models/enums/login-variant';
import { Tooltip } from '@mui/material';

const OrganizationUserAccessSettings: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const organizationData = useAppSelector((state) => selectOrganizationsData(state));
  const currentUser = useAppSelector((state) => selectUser(state));

  const [isConfirmAbortDialog2faOpen, setIsConfirmAbortDialog2faOpen] = useState(false);
  const [isConfirmAbortDialog2faDisableOpen, setIsConfirmAbortDialog2faDisableOpen] =
    useState(false);
  const [isConfirmAbortDialogEnforceSSOOpen, setIsConfirmAbortDialogEnforceSSOOpen] =
    useState(false);

  useEffect(() => {
    if (
      organizationData.rest.patchStatus === HttpFetchStatus.Success &&
      isConfirmAbortDialog2faOpen
    ) {
      setIsConfirmAbortDialog2faOpen(false);
      if (
        currentUser.data.organization.id === organizationData.current?.id &&
        !currentUser.data.isTwoFactorConfigured
      ) {
        removeToken();
        window.location.reload();
      }
    }
  }, [
    currentUser.data.isTwoFactorConfigured,
    currentUser.data.organization.id,
    isConfirmAbortDialog2faOpen,
    organizationData,
    organizationData.rest.patchStatus,
  ]);

  const toggleWireguardEnabledHandler = () => {
    if (organizationData.current?.id != null) {
      dispatch(
        patchOrganizationById({
          id: organizationData.current.id,
          data: { allowWireguardAccess: !organizationData.current.allowWireguardAccess },
        }),
      );
    }
  };

  const toggleRobotInviteLinkHandler = () => {
    if (organizationData.current?.id != null) {
      dispatch(
        patchOrganizationById({
          id: organizationData.current.id,
          data: { acceptMembersByInviteLink: !organizationData.current.acceptMembersByInviteLink },
        }),
      );
    }
  };

  const patchRequireTwoFactorHandler = () => {
    setIsConfirmAbortDialog2faDisableOpen(false);
    if (organizationData.current?.id != null) {
      dispatch(
        patchOrganizationById({
          id: organizationData.current.id,
          data: { require2fa: !organizationData.current.require2fa },
        }),
      );
    }
  };

  const patchEnforceSSOHandler = () => {
    setIsConfirmAbortDialogEnforceSSOOpen(false);
    if (organizationData.current?.id != null) {
      dispatch(
        patchOrganizationById({
          id: organizationData.current.id,
          data: { enforceSSO: !organizationData.current.enforceSSO },
        }),
      );
    }
  };

  const toggleSkipThirdPartyTwoFactorHandler = () => {
    if (organizationData.current?.id != null) {
      dispatch(
        patchOrganizationById({
          id: organizationData.current.id,
          data: { skipThirdPartyTwoFa: !organizationData.current.skipThirdPartyTwoFa },
        }),
      );
    }
  };

  const closeSnackbarHandler = () => {
    dispatch(
      resetOrganizationsRestState({
        patchStatus: HttpFetchStatus.Idle,
        patchError: null,
      }),
    );
  };

  const toggleRequireTwoFactorHandler = () => {
    if (organizationData.current?.require2fa) {
      setIsConfirmAbortDialog2faDisableOpen(true);
    } else {
      setIsConfirmAbortDialog2faOpen(true);
    }
  };

  const toggleEnforceSSOHandler = () => {
    if (organizationData.current?.enforceSSO) {
      patchEnforceSSOHandler();
    } else {
      setIsConfirmAbortDialogEnforceSSOOpen(true);
    }
  };

  const abortRequireTwoFactorChangeHandler = () => {
    setIsConfirmAbortDialog2faOpen(false);
  };

  const abortDisableTwoFactorChangeHandler = () => {
    setIsConfirmAbortDialog2faDisableOpen(false);
  };

  const abortEnforceSSOHandler = () => {
    setIsConfirmAbortDialogEnforceSSOOpen(false);
  };

  return (
    <React.Fragment>
      <Box p={0.3}>
        <CustomSplitColumnCard
          title={t('title-organization-user-connections')}
          content={[
            currentUser.data.organization.role === OrganizationRole.MANUFACTURER
              ? {
                  left: (
                    <Stack direction="row" alignItems="center" spacing={1} py={1}>
                      <UsersIcon />
                      <Typography>{t('title-organization-user-settings')}</Typography>
                    </Stack>
                  ),
                  right: (
                    <Stack p={2} spacing={2}>
                      <SwitchInput
                        adminOnly
                        label={
                          <Stack display="inline-flex" spacing={0.5}>
                            <Typography>{t('input-label-wireguard-enabled')}</Typography>
                            <Typography variant="caption">
                              {t('form-help-text-wireguard-enabled-for-user')}
                            </Typography>
                          </Stack>
                        }
                        isChecked={organizationData.current?.allowWireguardAccess ?? false}
                        onChange={toggleWireguardEnabledHandler}
                      />
                    </Stack>
                  ),
                }
              : null,
            {
              left: (
                <Stack direction="row" alignItems="center" spacing={1} py={1}>
                  <RobotIcon />
                  <Typography>{t('title-organization-robot-settings')}</Typography>
                </Stack>
              ),
              right: (
                <Stack p={2} spacing={2}>
                  <SwitchInput
                    adminOnly
                    label={
                      <Stack display="inline-flex" spacing={0.5}>
                        <Typography>{t('input-label-accept-members-by-invite-link')}</Typography>
                        <Typography variant="caption">
                          {t('form-help-text-accept-members-by-invite-link')}
                        </Typography>
                      </Stack>
                    }
                    isChecked={organizationData.current?.acceptMembersByInviteLink ?? false}
                    onChange={toggleRobotInviteLinkHandler}
                  />
                </Stack>
              ),
            },
            {
              left: (
                <Stack direction="row" alignItems="center" spacing={1} py={1}>
                  <PasswordIcon />
                  <Typography>{t('title-organization-authentication')}</Typography>
                </Stack>
              ),
              right: (
                <Stack p={2} spacing={2}>
                  <SwitchInput
                    adminOnly
                    label={
                      <Stack display="inline-flex" spacing={0.5}>
                        <Typography>{t('input-label-require-two-factor')}</Typography>
                        <Typography variant="caption">
                          {t('form-help-text-require-two-factor')}
                        </Typography>
                      </Stack>
                    }
                    isChecked={organizationData.current?.require2fa ?? false}
                    onChange={toggleRequireTwoFactorHandler}
                  />
                  <SwitchInput
                    adminOnly
                    label={
                      <Stack display="inline-flex" spacing={0.5}>
                        <Typography>{t('input-label-skip-third-party-two-factor')}</Typography>
                        <Typography variant="caption">
                          {t('form-help-text-skip-third-party-two-factor')}
                        </Typography>
                      </Stack>
                    }
                    isChecked={organizationData.current?.skipThirdPartyTwoFa ?? false}
                    onChange={toggleSkipThirdPartyTwoFactorHandler}
                  />
                  <Tooltip
                    title={
                      currentUser.data.organization.id === organizationData.current?.id &&
                      currentUser.data.lastLoginVariant === LoginVariant.Local
                        ? t('tooltip-cannot-enforce-sso-if-not-logged-in-with-sso')
                        : undefined
                    }
                  >
                    <span>
                      <SwitchInput
                        adminOnly
                        disabled={
                          currentUser.data.organization.id === organizationData.current?.id &&
                          currentUser.data.lastLoginVariant === LoginVariant.Local
                        }
                        label={
                          <Stack display="inline-flex" spacing={0.5}>
                            <Typography>{t('input-label-enforce-sso-login')}</Typography>
                            <Typography variant="caption">
                              {t('form-help-text-enforce-sso-login')}
                            </Typography>
                          </Stack>
                        }
                        isChecked={organizationData.current?.enforceSSO ?? false}
                        onChange={toggleEnforceSSOHandler}
                      />
                    </span>
                  </Tooltip>
                </Stack>
              ),
            },
          ]}
        />
      </Box>
      <ConfirmAbortDialog
        isOpen={isConfirmAbortDialog2faOpen}
        title={t('title-confirm-enable-two-factor-auth')}
        description={t('info-confirm-enable-two-factor-auth', {
          organization: organizationData.current?.name,
        })}
        abortButtonText={t('button-cancel')}
        confirmButtonText={t('button-confirm')}
        onConfirm={patchRequireTwoFactorHandler}
        onAbort={abortRequireTwoFactorChangeHandler}
      />
      <ConfirmAbortDialog
        isOpen={isConfirmAbortDialog2faDisableOpen}
        title={t('title-confirm-disable-two-factor-auth')}
        description={t('info-confirm-disable-two-factor-auth', {
          organization: organizationData.current?.name,
        })}
        abortButtonText={t('button-cancel')}
        confirmButtonText={t('button-confirm')}
        onConfirm={patchRequireTwoFactorHandler}
        onAbort={abortDisableTwoFactorChangeHandler}
      />
      <ConfirmAbortDialog
        isOpen={isConfirmAbortDialogEnforceSSOOpen}
        title={t('title-confirm-enforce-sso-login')}
        description={t('info-confirm-enforce-sso-login', {
          organization: organizationData.current?.name,
        })}
        abortButtonText={t('button-cancel')}
        confirmButtonText={t('button-confirm')}
        onConfirm={patchEnforceSSOHandler}
        onAbort={abortEnforceSSOHandler}
      />
      <CustomSnackbar
        open={organizationData.rest.patchStatus === HttpFetchStatus.Success}
        title={t('success-patching-' + organizationData.current?.role)}
        onClose={closeSnackbarHandler}
      />
    </React.Fragment>
  );
};

export default OrganizationUserAccessSettings;
